import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Copy,
    Loader2,
    Mail,
    Minus,
    Plus,
    ShieldCheck,
    Trash2,
    UserMinus,
    Users,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function TeamWorkspace() {
    const { user, refresh } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviting, setInviting] = useState(false);
    const [seatBusy, setSeatBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data: d } = await api.get("/team");
            setData(d);
        } catch {
            toast.error("Could not load team workspace");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div className="container-edit py-14">
                <p className="text-[#0D1B3E]/60 flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Loading team…
                </p>
            </div>
        );
    }

    if (!data?.team) return <NoTeam />;

    const { team, seats, usage, members, invites } = data;
    const myRole = data.my_role || user?.team_role;
    const canManage = myRole === "owner" || myRole === "admin";
    const isOwner = myRole === "owner";
    const pct = usage.pooled_limit
        ? Math.min(100, Math.round((usage.pooled_used / usage.pooled_limit) * 100))
        : 0;

    async function sendInvite(e) {
        e.preventDefault();
        if (!inviteEmail.trim()) return;
        setInviting(true);
        try {
            await api.post("/team/invites", { email: inviteEmail.trim() });
            toast.success(`Invite created for ${inviteEmail.trim()}`);
            setInviteEmail("");
            await load();
        } catch (err) {
            toast.error(detail(err) || "Could not create invite");
        } finally {
            setInviting(false);
        }
    }

    async function revokeInvite(id) {
        try {
            await api.delete(`/team/invites/${id}`);
            toast("Invite revoked");
            await load();
        } catch (err) {
            toast.error(detail(err) || "Could not revoke invite");
        }
    }

    async function removeMember(m) {
        try {
            await api.delete(`/team/members/${m.id}`);
            toast(`${m.email} removed from the team`);
            await load();
        } catch (err) {
            toast.error(detail(err) || "Could not remove member");
        }
    }

    async function changeRole(m, role) {
        try {
            await api.patch(`/team/members/${m.id}`, { team_role: role });
            toast.success(`${m.email} is now ${role}`);
            await load();
        } catch (err) {
            toast.error(detail(err) || "Could not change role");
        }
    }

    async function changeSeats(delta) {
        const next = seats.total + delta;
        setSeatBusy(true);
        try {
            await api.post("/team/seats", { seats_total: next });
            toast.success(`Seats updated to ${next}`);
            await load();
            await refresh();
        } catch (err) {
            toast.error(detail(err) || "Could not change seats");
        } finally {
            setSeatBusy(false);
        }
    }

    function copyInviteLink(token) {
        const url = `${window.location.origin}/invite/${token}`;
        navigator.clipboard?.writeText(url)
            .then(() => toast.success("Invite link copied"))
            .catch(() => toast(url));
    }

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="team-page">
            <div className="label-eyebrow">Team workspace</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight" data-testid="team-name">
                {team.name}
            </h1>
            <p className="mt-3 max-w-2xl text-[#0D1B3E]/75">
                Shared credits, seat management and member roles for your workspace.
                {myRole && <> Your role: <strong className="capitalize">{myRole}</strong>.</>}
            </p>

            {/* Seats + usage */}
            <div className="mt-8 grid gap-4 md:grid-cols-3">
                <div className="card-flat p-6" data-testid="team-seats-card">
                    <div className="label-eyebrow">Seats</div>
                    <div className="mt-2 font-display text-3xl tracking-tight">
                        {seats.active + seats.pending} <span className="text-lg text-[#0D1B3E]/50">/ {seats.total}</span>
                    </div>
                    <p className="mt-1 text-xs text-[#0D1B3E]/60">
                        {seats.active} active · {seats.pending} pending · {seats.available} available
                    </p>
                    {isOwner && (
                        <div className="mt-4 flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => changeSeats(-1)}
                                disabled={seatBusy || seats.total <= Math.max(3, seats.active + seats.pending)}
                                className="w-9 h-9 border border-[#0D1B3E]/20 flex items-center justify-center hover:bg-[#0D1B3E]/5 disabled:opacity-40"
                                data-testid="team-seats-remove"
                                aria-label="Remove a seat"
                            >
                                <Minus size={14} />
                            </button>
                            <button
                                type="button"
                                onClick={() => changeSeats(1)}
                                disabled={seatBusy || seats.total >= 25}
                                className="w-9 h-9 border border-[#0D1B3E]/20 flex items-center justify-center hover:bg-[#0D1B3E]/5 disabled:opacity-40"
                                data-testid="team-seats-add"
                                aria-label="Add a seat"
                            >
                                <Plus size={14} />
                            </button>
                            <span className="text-[11px] text-[#0D1B3E]/55">
                                €{team.extra_seat_eur}/mo per seat beyond {team.included_seats}
                            </span>
                        </div>
                    )}
                </div>

                <div className="card-flat p-6 md:col-span-2" data-testid="team-usage-card">
                    <div className="label-eyebrow">Pooled credits · {usage.period}</div>
                    <div className="mt-2 flex items-baseline gap-2">
                        <span className="font-display text-3xl tracking-tight" data-testid="team-usage-used">
                            {usage.pooled_used.toLocaleString()}
                        </span>
                        <span className="text-sm text-[#0D1B3E]/55">
                            of {usage.pooled_limit.toLocaleString()} credits used
                        </span>
                    </div>
                    <div className="mt-3 h-2 bg-[#0D1B3E]/10 overflow-hidden">
                        <div
                            className="h-full bg-[#C9A84C] transition-all"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <p className="mt-2 text-xs text-[#0D1B3E]/55">
                        Every member draws from the same pool. Per-member usage is shown below.
                    </p>
                </div>
            </div>

            {/* Invite */}
            {canManage && (
                <section className="mt-10">
                    <div className="label-eyebrow">Invite members</div>
                    <h2 className="font-display text-2xl mt-1 tracking-tight">Bring your colleagues in.</h2>
                    <form onSubmit={sendInvite} className="mt-4 flex flex-wrap gap-3 max-w-xl" data-testid="team-invite-form">
                        <div className="relative flex-1 min-w-[240px]">
                            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50" />
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="colleague@firm.eu"
                                className="field pl-9"
                                required
                                data-testid="team-invite-email-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={inviting || seats.available <= 0}
                            className="btn-primary disabled:opacity-60"
                            data-testid="team-invite-submit"
                        >
                            {inviting ? <Loader2 size={14} className="animate-spin" /> : "Send invite"}
                        </button>
                    </form>
                    {seats.available <= 0 && (
                        <p className="mt-2 text-xs text-[#8B1E1E]" data-testid="team-invite-noseats">
                            All seats are taken or reserved. Add seats to invite more members.
                        </p>
                    )}

                    {invites.length > 0 && (
                        <ul className="mt-5 space-y-2 max-w-2xl" data-testid="team-invites-list">
                            {invites.map((inv) => (
                                <li
                                    key={inv.id}
                                    className="card-flat px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                                    data-testid={`team-invite-${inv.id}`}
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{inv.email}</div>
                                        <div className="text-[11px] text-[#0D1B3E]/55">
                                            Pending · invited by {inv.invited_by || "team"} · expires{" "}
                                            {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "–"}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => copyInviteLink(inv.token)}
                                            className="btn-secondary !py-1.5 !px-3 text-xs"
                                            data-testid={`team-invite-copy-${inv.id}`}
                                        >
                                            <Copy size={12} /> Copy link
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => revokeInvite(inv.id)}
                                            className="text-[#8B1E1E] hover:opacity-70 p-2"
                                            title="Revoke invite"
                                            data-testid={`team-invite-revoke-${inv.id}`}
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {/* Members */}
            <section className="mt-10">
                <div className="label-eyebrow">Members</div>
                <h2 className="font-display text-2xl mt-1 tracking-tight">
                    {members.length} of {seats.total} seats in use.
                </h2>
                <ul className="mt-5 space-y-2 max-w-3xl" data-testid="team-members-list">
                    {members.map((m) => (
                        <li
                            key={m.id}
                            className="card-flat px-5 py-4 flex items-center justify-between gap-4 flex-wrap"
                            data-testid={`team-member-${m.id}`}
                        >
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium">{m.name || m.email}</span>
                                    <RoleBadge role={m.team_role} />
                                    {m.id === user?.id && (
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/50">you</span>
                                    )}
                                </div>
                                <div className="text-[11px] text-[#0D1B3E]/55 truncate">{m.email}</div>
                            </div>
                            <div className="flex items-center gap-4 flex-wrap">
                                <span className="text-xs text-[#0D1B3E]/70" data-testid={`team-member-usage-${m.id}`}>
                                    {m.credits_used.toLocaleString()} credits this month
                                </span>
                                {isOwner && m.team_role !== "owner" && (
                                    <select
                                        value={m.team_role}
                                        onChange={(e) => changeRole(m, e.target.value)}
                                        className="field !w-auto !py-1.5 text-xs"
                                        data-testid={`team-member-role-${m.id}`}
                                    >
                                        <option value="member">Member</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                )}
                                {canManage && m.team_role !== "owner" && m.id !== user?.id && (
                                    <button
                                        type="button"
                                        onClick={() => removeMember(m)}
                                        className="text-[#8B1E1E] hover:opacity-70 p-2"
                                        title="Remove member"
                                        data-testid={`team-member-remove-${m.id}`}
                                    >
                                        <UserMinus size={16} />
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </section>

            <div className="mt-12 flex flex-wrap gap-3">
                <Link to="/app/billing/portal" className="btn-secondary" data-testid="team-billing-link">
                    <ShieldCheck size={14} /> Manage subscription
                </Link>
            </div>
        </div>
    );
}

function RoleBadge({ role }) {
    const style =
        role === "owner"
            ? "bg-[#0D1B3E] text-[#F5F0E8]"
            : role === "admin"
              ? "bg-[#C9A84C]/20 text-[#7a6222] border border-[#C9A84C]/50"
              : "border border-[#0D1B3E]/20 text-[#0D1B3E]/70";
    return (
        <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 ${style}`}>
            {role}
        </span>
    );
}

function NoTeam() {
    return (
        <div className="container-edit py-14 max-w-2xl" data-testid="team-empty-state">
            <Users size={32} color="#C9A84C" />
            <h1 className="font-display text-3xl lg:text-4xl mt-4 tracking-tight">
                No team workspace yet.
            </h1>
            <p className="mt-4 text-[#0D1B3E]/75">
                The Team plan gives you a shared workspace: pooled credits, member invites
                with seat management, per-member usage tracking, and heavy features like
                the Argument Generator and Comparative Law for everyone.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/app/pricing" className="btn-primary" data-testid="team-empty-upgrade">
                    Explore the Team plan
                </Link>
            </div>
        </div>
    );
}

function detail(err) {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    return d?.message || null;
}
